import React, { useState } from 'react';
import { Gift, Award, Clock, TrendingUp, X, ChevronRight } from 'lucide-react';

const WelcomeModal = ({ isOpen, onClose, userName }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to High Quality Studios! 🎉",
      icon: <Gift className="text-purple-400" size={48} />,
      content: (
        <>
          <p className="text-gray-300 mb-4">
            Hey {userName}! We're excited to have you. Let's show you how our rewards system works.
          </p>
          <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4">
            <p className="text-white font-semibold mb-2">🎁 Loyalty Rewards Program</p>
            <p className="text-gray-400 text-sm">
              Every time you book studio time, you earn credits toward FREE hours!
            </p>
          </div>
        </>
      )
    },
    {
      title: "How to Earn Credits",
      icon: <Award className="text-yellow-400" size={48} />,
      content: (
        <>
          <p className="text-gray-300 mb-4">
            It's simple - the more you record, the more you earn!
          </p>
          <div className="space-y-3">
            <div className="bg-black/40 border border-purple-500/20 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-purple-600/30 rounded-full w-8 h-8 flex items-center justify-center">
                  <span className="text-purple-300 font-bold">1</span>
                </div>
                <p className="text-white font-semibold">Book studio sessions</p>
              </div>
              <p className="text-gray-400 text-sm pl-11">
                Recording, mixing, or any service counts!
              </p>
            </div>
            
            <div className="bg-black/40 border border-purple-500/20 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-yellow-600/30 rounded-full w-8 h-8 flex items-center justify-center">
                  <span className="text-yellow-300 font-bold">2</span>
                </div>
                <p className="text-white font-semibold">Earn 1 credit per 3 hours</p>
              </div>
              <p className="text-gray-400 text-sm pl-11">
                Every 3 hours of studio time = 1 credit
              </p>
            </div>

            <div className="bg-black/40 border border-purple-500/20 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-green-600/30 rounded-full w-8 h-8 flex items-center justify-center">
                  <span className="text-green-300 font-bold">3</span>
                </div>
                <p className="text-white font-semibold">Redeem 3 credits = 1 FREE hour!</p>
              </div>
              <p className="text-gray-400 text-sm pl-11">
                Save up 3 credits to get a free hour in the studio
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      title: "Track Your Progress",
      icon: <TrendingUp className="text-blue-400" size={48} />,
      content: (
        <>
          <p className="text-gray-300 mb-4">
            Check your stats anytime by clicking your profile menu!
          </p>
          <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-lg p-4">
            <p className="text-white font-semibold mb-3">📊 Your Tracker Shows:</p>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-center gap-2">
                <Clock size={16} className="text-blue-400" />
                <span>Monthly hours booked</span>
              </li>
              <li className="flex items-center gap-2">
                <Award size={16} className="text-yellow-400" />
                <span>Credits available to redeem</span>
              </li>
              <li className="flex items-center gap-2">
                <TrendingUp size={16} className="text-green-400" />
                <span>Total lifetime hours</span>
              </li>
              <li className="flex items-center gap-2">
                <Gift size={16} className="text-purple-400" />
                <span>Next credit countdown</span>
              </li>
            </ul>
          </div>
          <div className="mt-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
            <p className="text-yellow-200 text-sm">
              💡 <span className="font-semibold">Pro Tip:</span> Click your name in the top-right corner anytime to see your stats!
            </p>
          </div>
        </>
      )
    },
    {
      title: "You're All Set! 🚀",
      icon: <Gift className="text-green-400" size={48} />,
      content: (
        <>
          <p className="text-gray-300 mb-4">
            Ready to start earning those free hours?
          </p>
          <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-lg p-4 mb-4">
            <p className="text-white font-semibold mb-2">Quick Recap:</p>
            <ul className="space-y-1 text-sm text-gray-300">
              <li>✅ Book sessions to earn credits</li>
              <li>✅ 3 hours = 1 credit</li>
              <li>✅ 3 credits = 1 FREE hour</li>
              <li>✅ Track progress in your profile menu</li>
            </ul>
          </div>
          <p className="text-gray-400 text-sm text-center">
            Head to the <span className="text-purple-400 font-semibold">Book Now</span> section to schedule your first session!
          </p>
        </>
      )
    }
  ];

  if (!isOpen) return null;

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onClose();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md">
      <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-purple-500/50 rounded-2xl p-6 sm:p-8 max-w-lg w-full shadow-2xl shadow-purple-500/20">
        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          {currentStepData.icon}
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-white text-center mb-4">
          {currentStepData.title}
        </h2>

        {/* Content */}
        <div className="mb-6">
          {currentStepData.content}
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-2 mb-6">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all ${
                index === currentStep
                  ? 'w-8 bg-purple-500'
                  : index < currentStep
                  ? 'w-2 bg-purple-400'
                  : 'w-2 bg-gray-600'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          {!isLastStep && (
            <button
              onClick={handleSkip}
              className="flex-1 px-6 py-3 text-gray-400 hover:text-white transition-colors"
            >
              Skip
            </button>
          )}
          <button
            onClick={handleNext}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all"
          >
            {isLastStep ? "Let's Go!" : "Next"}
            {!isLastStep && <ChevronRight size={20} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
